import { useRef, useState } from "react";

import { api } from "@/lib/api";
import { compressImage } from "@/lib/imageUtils";
import type { AuthUser } from "@/types";
import { BrandLogo } from "@/components/shared/common/BrandLogo";

interface OnboardingPageProps {
  authUser: AuthUser;
  onComplete: (user: AuthUser, token: string) => void;
}

function toSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function OnboardingPage({ authUser, onComplete }: OnboardingPageProps) {
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [previewLogo, setPreviewLogo] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);

  const [previewBanner, setPreviewBanner] = useState<string | null>(null);
  const [bannerUrl, setBannerUrl] = useState<string | null>(null);
  const [bannerUploading, setBannerUploading] = useState(false);

  const logoInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);

  const slug = toSlug(name);
  const initial = name.trim().charAt(0).toUpperCase() || "?";

  const handleLogoFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setLogoUploading(true);
    try {
      const { dataUrl, mime } = await compressImage(file, 256, 256, 0.85);
      setPreviewLogo(dataUrl);
      const { url } = await api.uploadImage({ imageData: dataUrl, mime, slot: `org-logo-onboard-${authUser.id}` });
      setLogoUrl(url);
    } catch {
      setError("Failed to upload logo. Please try again.");
    } finally {
      setLogoUploading(false);
    }
  };

  const handleBannerFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setBannerUploading(true);
    try {
      const { dataUrl, mime } = await compressImage(file, 1200, 400, 0.82);
      setPreviewBanner(dataUrl);
      const { url } = await api.uploadImage({ imageData: dataUrl, mime, slot: `org-banner-onboard-${authUser.id}` });
      setBannerUrl(url);
    } catch {
      setError("Failed to upload banner. Please try again.");
    } finally {
      setBannerUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setError("");
    setLoading(true);
    try {
      const { token, user } = await api.onboard(name.trim(), logoUrl, bannerUrl);
      onComplete(user, token);
    } catch (err: any) {
      const msg: string = err.message ?? "";
      if (msg.includes("already exists")) {
        setError("An organization with a similar name already exists. Try a different name.");
      } else {
        setError("Failed to create organization. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const busy = loading || logoUploading || bannerUploading;

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-secondary px-4 py-10">
      <div className="w-full max-w-lg rounded-card bg-white shadow-card overflow-hidden">

        {/* Banner upload zone */}
        <div className="relative h-36 bg-[#f2f2f2]">
          {previewBanner ? (
            <img src={previewBanner} alt="banner preview" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center">
              <svg className="h-8 w-8 text-[#c1c1c1]" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M13.5 12a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
              </svg>
            </div>
          )}

          <button
            type="button"
            onClick={() => bannerInputRef.current?.click()}
            disabled={bannerUploading}
            className="absolute bottom-2 right-2 flex items-center gap-1.5 rounded-lg bg-white/90 px-3 py-1.5 text-xs font-medium text-near-black shadow-sm hover:bg-white transition disabled:opacity-50"
          >
            {bannerUploading ? (
              <span className="h-3 w-3 animate-spin rounded-full border border-border-card border-t-rausch inline-block" />
            ) : (
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" />
              </svg>
            )}
            {previewBanner ? "Change banner" : "Upload banner"}
          </button>
          <input ref={bannerInputRef} type="file" accept="image/*" className="hidden" onChange={handleBannerFile} />
        </div>

        {/* Logo overlapping banner */}
        <div className="relative px-6">
          <div className="absolute -top-9 left-6">
            <div className="relative h-[72px] w-[72px]">
              {previewLogo ? (
                <img src={previewLogo} alt="logo preview" className="h-full w-full rounded-full border-2 border-white bg-white object-cover shadow-md" />
              ) : (
                <div className="flex h-full w-full items-center justify-center rounded-full border-2 border-white bg-[#f2f2f2] shadow-md">
                  <span className="text-2xl font-bold text-[#c1c1c1]">{initial}</span>
                </div>
              )}
              <button
                type="button"
                onClick={() => logoInputRef.current?.click()}
                disabled={logoUploading}
                aria-label="Upload logo"
                className="absolute bottom-0 right-0 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-sm border border-border-card hover:bg-[#f2f2f2] transition disabled:opacity-50"
              >
                {logoUploading ? (
                  <span className="h-3 w-3 animate-spin rounded-full border border-border-card border-t-rausch inline-block" />
                ) : (
                  <svg className="h-3 w-3 text-near-black" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" />
                  </svg>
                )}
              </button>
              <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoFile} />
            </div>
          </div>

          {/* Brand logo top-right */}
          <div className="flex justify-end pt-2 pb-0">
            <BrandLogo imgClassName="h-8 w-auto opacity-70" />
          </div>
        </div>

        {/* Form body */}
        <form onSubmit={handleSubmit} className="px-6 pb-8 pt-10">
          <h1 className="mb-1 text-xl font-bold text-near-black">Set up your organization</h1>
          <p className="mb-5 text-sm text-text-secondary">
            Create your organization to start managing candidates and positions.
          </p>

          <div className="mb-4">
            <label htmlFor="org-name" className="mb-1.5 block text-sm font-medium text-near-black">
              Organization name <span className="text-rausch">*</span>
            </label>
            <input
              id="org-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Corp"
              required
              autoFocus
              className="w-full rounded-lg border border-border-card px-4 py-2.5 text-sm text-near-black placeholder-text-secondary outline-none transition focus:border-rausch focus:ring-1 focus:ring-rausch"
            />
            {slug && (
              <p className="mt-1.5 text-xs text-text-secondary">
                Slug: <span className="font-medium text-near-black">{slug}</span>
              </p>
            )}
          </div>

          {error && (
            <p className="mb-4 rounded-lg bg-red-50 px-4 py-2.5 text-sm text-red-600">{error}</p>
          )}

          <button
            type="submit"
            disabled={busy || !name.trim()}
            className="w-full rounded-lg bg-rausch px-4 py-2.5 text-sm font-medium text-white transition hover:bg-rausch-dark disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? "Creating…" : "Create organization"}
          </button>
        </form>
      </div>
    </div>
  );
}
